'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, type ReactNode } from 'react';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  titleId?: string;
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
  variant?: ModalVariant;
};

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const defaultCard =
  'relative w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-8 my-[10vh] shadow-[0_5px_15px_rgba(0,0,0,0.5)]';

const VARIANT_CLASSES = {
  default: {
    backdrop: 'backdrop:bg-black/50',
    card: defaultCard,
  },
  brand: {
    backdrop: 'backdrop:bg-primary/60',
    card: 'relative w-full max-w-2xl overflow-y-auto rounded-2xl bg-secondary-lighter p-8 mt-[10vh] mx-4',
  },
} as const;

export type ModalVariant = keyof typeof VARIANT_CLASSES;

export const Modal = ({
  open,
  onClose,
  titleId,
  ariaLabel,
  children,
  className,
  variant = 'default',
}: ModalProps) => {
  const t = useTranslations('common');
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }

    if (!open) {
      if (dialog.open) {
        dialog.close();
      }
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    if (!dialog.open) {
      dialog.showModal();
    }

    const focusable = dialog.querySelectorAll<HTMLElement>(FOCUSABLE);
    (focusable[0] ?? dialog).focus();

    const trapFocus = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') {
        return;
      }
      const els = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (els.length === 0) {
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener('keydown', trapFocus);

    return () => {
      dialog.removeEventListener('keydown', trapFocus);
      if (dialog.open) {
        dialog.close();
      }
      previouslyFocused?.focus();
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-label={!titleId ? ariaLabel : undefined}
      className={classNames(
        'm-0 h-dvh max-h-dvh w-full max-w-none flex-col items-center overflow-y-auto border-0 bg-transparent px-4 open:flex',
        VARIANT_CLASSES[variant].backdrop,
      )}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={classNames(VARIANT_CLASSES[variant].card, className)}>
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl leading-none font-bold text-black opacity-20 transition-opacity hover:opacity-50"
          aria-label={t('close')}
        >
          <span aria-hidden="true">×</span>
        </button>
        {children}
      </div>
    </dialog>
  );
};
